import type React from 'react';

import { type PropsWithChildren } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { type UserBasicDto } from '@/api/generated/models/user-basic-dto';
import { useGetUserLandingPage } from '@/api/users/useGetUserLandingPage';
import FullPageSpinner from '@/components/ui/fullPageSpinner/FullPageSpinner';
import { syncCurrentUserSnapshot } from '@/contexts/userContext/currentUserSnapshot.util.ts';
import {
  UserContext,
  type UserContextProps,
  UserDefault,
  type UserModel,
} from '@/contexts/userContext/User.context.ts';
import { landingPageTypeToRoute } from '@/contexts/userContext/util/landingPageTypeToRoute.util';
import { useGetCurrentUser } from '@/contexts/userContext/util/useGetCurrentUser.util.ts';
import { RouteEnum } from '@/layouts/routes/Route.model.ts';
import { PATHS_NOT_REQUIRING_AUTHORIZATION } from '@/model/routes.consts.ts';
import { useOcdHistory } from '@/utils/useOcdHistory.util';

export const UserContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [userState, setUserState] = useState<UserModel>(UserDefault);
  const [isInitialized, setIsInitialized] = useState(false);

  // Mirrors every context update into the loader-facing snapshot (OCD-309)
  // so guard decisions stay in step with what components see.
  const setUser: UserContextProps['setUser'] = value => {
    setUserState(prevUser => {
      const nextUser = typeof value === 'function' ? value(prevUser) : value;
      syncCurrentUserSnapshot(nextUser);
      return nextUser;
    });
  };

  const { location } = useOcdHistory();

  const pathWithoutSession = PATHS_NOT_REQUIRING_AUTHORIZATION.map(path =>
    path.toString(),
  ).includes(location.pathname);

  const { currentUser } = useGetCurrentUser({ pathWithoutSession });

  const { isLoading: isLoadingLandingPage, landingPage } = useGetUserLandingPage({
    enabled: Boolean(currentUser?.id) && !pathWithoutSession,
    userId: currentUser?.id ?? '',
  });

  const setUserHelper = (newUser: UserBasicDto, defaultPath: RouteEnum) => {
    setUser({
      ...newUser,
      dataReady: true,
      defaultPath,
      fullName: `${newUser.firstName} ${newUser.lastName}`,
      userId: newUser.id,
    });
  };

  const init = async () => {
    if (pathWithoutSession) {
      setIsInitialized(true);
      setUser({
        ...userState,
        dataReady: true,
      });
      return;
    }

    if (!currentUser) {
      console.error('User is not defined');
      return;
    }

    if (landingPage)
      setUserHelper(
        currentUser,
        landingPage ? landingPageTypeToRoute[landingPage] : RouteEnum.RECRUITMENT_BOARD,
      );
    setIsInitialized(true);
  };

  useEffect(() => {
    if (globalThis.location.pathname === '/logout') {
      return;
    }

    if (!pathWithoutSession && currentUser && isLoadingLandingPage) {
      return;
    }

    setIsInitialized(false);
    void init();
  }, [currentUser, landingPage, isLoadingLandingPage]);

  const userContext = useMemo<UserContextProps>(() => ({ setUser, user: userState }), [userState]);

  if (!isInitialized) {
    return <FullPageSpinner />;
  }

  return <UserContext value={userContext}>{children}</UserContext>;
};
